import { useCallback, useMemo } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useSuspenseQuery } from '@tanstack/react-query';
import {
    createColumnHelper,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import * as Avatar from '@givebutter/primitives/avatar';
import { Box } from '@givebutter/primitives/box';
import {
    faArrowUpRightFromSquare,
    faCoins,
    faPenToSquare,
    faTrash,
    faUser,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FetchCampaignResourceParams, fetchCampaignMembers } from 'api/campaigns';
import { useCampaignMembers } from 'assets/js/pages/campaign-settings/campaign-members/CampaignMembersProvider';
import * as DeleteMemberDialog from 'assets/js/pages/campaign-settings/campaign-members/DeleteMemberDialog';
import { RaisedTotal } from 'assets/js/pages/campaigns/RaisedTotal';
import { CopyButton } from 'components/copy-button';
import * as DropdownMenu from 'components/dropdown-menu';
import { transformSavedFilterGroups } from 'components/filter/filters-next/downstream';
import * as Table from 'components/table';
import { useSyncPaginatedData } from 'components/table/useSyncPaginatedData';
import * as Tooltip from 'components/tooltip';
import { UnstyledAnchor } from 'components/unstyled-link';
import { useFiltersCache } from 'contexts/useFiltersCache';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import useDebounce from 'customHooks/useDebounce';
import { useIsPending } from 'customHooks/useIsPending';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { Member } from 'types/domain';
import { OutboundFilters } from 'types/request';

const columnHelper = createColumnHelper<Member>();

const CampaignMembersTable = () => {
    const history = useHistory();
    const match = useRouteMatch();
    const { accountId } = useCurrentAccount();
    const { data: campaign } = useCurrentCampaign();
    const campaignId = campaign.id;
    const { search } = useCampaignMembers();
    const debouncedSearch = useDebounce(search);
    const { pagination, setPagination } = Table.useReactTableState();
    const updateFilterByKey = useFiltersCache(state => state.updateFiltersNextByKey);

    const [isMembersPending, membersParams] = useIsPending<FetchCampaignResourceParams>(
        {
            page: pagination.pageIndex + 1,
            query: debouncedSearch,
        },
        500,
    );

    const { data: members } = useSuspenseQuery({
        queryKey: campaignQueryKeys.membersFiltered(campaignId, membersParams),
        queryFn: () => fetchCampaignMembers(campaignId, membersParams),
    });

    const handleViewCreditedTransactionsClick = useCallback(
        (campaignId: number, teamMemberId: number) => {
            const values: OutboundFilters = [
                {
                    type: 'and',
                    children: [
                        {
                            type: 'and',
                            children: {
                                campaigns: [
                                    {
                                        value: [campaignId.toString()],
                                        operator: '=',
                                        type: 'and',
                                    },
                                ],
                            },
                        },
                        {
                            type: 'and',
                            children: {
                                teamMembers: [
                                    {
                                        value: [teamMemberId.toString()],
                                        operator: '=',
                                        type: 'and',
                                    },
                                ],
                            },
                        },
                    ],
                },
            ];

            updateFilterByKey('transactions', transformSavedFilterGroups(values));
            history.push(`/accounts/${accountId}/transactions`);
        },
        [accountId, history, updateFilterByKey],
    );

    const columns = useMemo(
        () => [
            columnHelper.accessor('display_name', {
                header: 'Contact',
                cell: info => {
                    const member = info.row.original;
                    return (
                        <div className={'flex items-center gap-2'}>
                            <Avatar.Root size="xs">
                                <Avatar.Image
                                    alt={`Profile picture of ${member.display_name}`}
                                    src={member.picture}
                                />
                                <Avatar.Fallback aria-label={member.display_name}>
                                    {member.display_name[0]}
                                </Avatar.Fallback>
                            </Avatar.Root>
                            <div className={'flex flex-col'}>
                                <p className={'font-medium'}>{member.display_name}</p>
                                {member.email && (
                                    <div className={'flex items-center gap-1'}>
                                        <a href={`mailto:${member.email}`} className={'text-xs'}>
                                            {member.email}
                                        </a>
                                        <CopyButton text={member.email} />
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                },
                meta: {
                    className: 'w-[250px]',
                },
            }),
            columnHelper.accessor('raised', {
                header: 'Raised',
                cell: info => {
                    const member = info.row.original;

                    return <RaisedTotal className={'w-full'} model={member} />;
                },
                meta: {
                    className: 'h-20 w-[430px]',
                },
            }),
            columnHelper.display({
                id: Table.Constants.Actions,
                cell: info => {
                    const member = info.row.original;
                    const hasMemberContactId = member.contact_id !== null;

                    return (
                        <DropdownMenu.Root key={member.id}>
                            <div className={'flex w-full justify-end gap-2'}>
                                <div className={'flex items-center gap-2'}>
                                    <Tooltip.Root>
                                        <Tooltip.Trigger>
                                            <CopyButton text={member.url} />
                                        </Tooltip.Trigger>
                                        <Tooltip.Portal>
                                            <Tooltip.Content inverse>
                                                <p>Copy link</p>
                                                <Tooltip.Arrow inverse />
                                            </Tooltip.Content>
                                        </Tooltip.Portal>
                                    </Tooltip.Root>
                                    <div className={'flex items-center'}>
                                        <Tooltip.Root>
                                            <Tooltip.Trigger>
                                                <UnstyledAnchor
                                                    target={'_blank'}
                                                    rel="noopener noreferrer"
                                                    href={member.url}
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faArrowUpRightFromSquare}
                                                        size="sm"
                                                    />
                                                </UnstyledAnchor>
                                            </Tooltip.Trigger>
                                            <Tooltip.Portal>
                                                <Tooltip.Content inverse>
                                                    <p>View fundraising page</p>
                                                    <Tooltip.Arrow inverse />
                                                </Tooltip.Content>
                                            </Tooltip.Portal>
                                        </Tooltip.Root>
                                    </div>
                                    <Table.TableRowDropdownTrigger />
                                </div>
                            </div>
                            <DropdownMenu.Content sideOffset={5} align={'end'}>
                                {hasMemberContactId && (
                                    <DropdownMenu.LinkItem
                                        to={`/accounts/${accountId}/contacts/${member.contact_id}`}
                                    >
                                        <div className={'flex items-center gap-2'}>
                                            <FontAwesomeIcon icon={faUser} />
                                            <span>View contact details</span>
                                        </div>
                                    </DropdownMenu.LinkItem>
                                )}
                                <DropdownMenu.Item
                                    onSelect={() =>
                                        handleViewCreditedTransactionsClick(campaignId, member.id)
                                    }
                                >
                                    <div className={'flex items-center gap-2'}>
                                        <FontAwesomeIcon icon={faCoins} />
                                        <span>View credited transactions</span>
                                    </div>
                                </DropdownMenu.Item>
                                <DropdownMenu.LinkItem to={`${match.url}/${member.id}`}>
                                    <div className={'flex items-center gap-2'}>
                                        <FontAwesomeIcon icon={faPenToSquare} />
                                        <span>Edit</span>
                                    </div>
                                </DropdownMenu.LinkItem>
                                <DeleteMemberDialog.Root memberId={member.id}>
                                    <DeleteMemberDialog.Trigger type={'reset'} asChild={true}>
                                        <DropdownMenu.Item onSelect={e => e.preventDefault()}>
                                            <div className={'flex items-center gap-2'}>
                                                <FontAwesomeIcon icon={faTrash} />
                                                <span>Delete</span>
                                            </div>
                                        </DropdownMenu.Item>
                                    </DeleteMemberDialog.Trigger>
                                </DeleteMemberDialog.Root>
                            </DropdownMenu.Content>
                        </DropdownMenu.Root>
                    );
                },
            }),
        ],
        [handleViewCreditedTransactionsClick, match.url, campaignId, accountId],
    );

    const table = useReactTable({
        data: members?.data ?? [],
        columns,
        pageCount: members?.meta.last_page ?? -1,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        state: {
            pagination,
        },
        onPaginationChange: setPagination,
        manualPagination: true,
        enableSorting: false,
        enableHiding: false,
        enableColumnPinning: false,
    });

    useSyncPaginatedData(table, members);

    return (
        <Table.Root isLoading={isMembersPending} variant={'bordered'} table={table}>
            <div className={'flex flex-col gap-y-4'}>
                <Box className={'overflow-x-auto'}>
                    <Table.Content>
                        <Table.Header />
                        <Table.Body />
                    </Table.Content>
                    <Table.Empty variant={'primary'}>
                        <Table.EmptyIcon />
                        <Table.EmptyTitle>No team members found</Table.EmptyTitle>
                        <Table.EmptyDescription className="min-w-[350px]">
                            {search
                                ? 'No team members match your search criteria.'
                                : 'Add team members to your campaign.'}
                        </Table.EmptyDescription>
                    </Table.Empty>
                </Box>
                <Table.Pagination>
                    <Table.PaginationPageNavigation />
                </Table.Pagination>
            </div>
        </Table.Root>
    );
};

export { CampaignMembersTable };
